import { useEffect, useState } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import { cn } from '@/lib/utils/classNames';
import {
  formatCacheCountdownMs,
  getAppCachePrefixSummaries,
  type AppCachePrefixSummary,
} from '@/lib/utils/indexedDbCache';

/**
 * Human labels for the cache key prefixes we know about. An unknown prefix falls back
 * to the raw prefix, so a newly added cache shows up in the list without any
 * registration step — the list itself comes from the IndexedDB store, not from here.
 */
const PREFIX_LABELS: Record<string, [en: string, ko: string]> = {
  'campaigns.getCampaigns::': ['Campaign list', '캠페인 목록'],
  'campaigns.loadActionLog::': ['Per-campaign admin action timestamps', '캠페인별 관리자 액션 시각'],
  'campaigns.getCampaignsStats::': ['Per-campaign stats (counts, unread)', '캠페인별 통계(건수, 미읽음 등)'],
  'api2.campaigns_budget_completion::': ['Budget completion per campaign', '캠페인별 예산 집행률'],
  'campaigns.uncheckedAdvertiserActionCounts::': ['Unchecked advertiser actions per campaign', '캠페인별 미확인 광고주 액션 수'],
  'campaigns.newPostCandidatesCount::': ['New post candidates per campaign', '캠페인별 신규 포스트 후보 수'],
  'advertisers.getAdvertisers::': ['Advertiser list', '광고주 목록'],
  'statsV2.loadResults::': ['Campaign results (analytics)', '캠페인 결과(애널리틱스)'],
  'panel.loadStats::': ['Panel: channel stats', '패널: 채널 통계'],
  'panel.loadCampaigns::': ['Panel: campaigns', '패널: 캠페인'],
  'panel.loadChannelLiveStreams::': ['Panel: live streams', '패널: 라이브 스트림'],
  'panel.loadPayments::': ['Panel: payments', '패널: 결제'],
  'panel.userProfileStats::': ['Panel: profile stats', '패널: 프로필 통계'],
  'dashboard.getUserWorkOrders::': ['Work orders per influencer', '인플루언서별 작업지시'],
};

interface CachedApiTargetsListProps {
  className?: string;
}

/**
 * What the IndexedDB API cache holds right now, one row per key prefix with its
 * remaining TTL. Shared by the header cache popover and /settings so both describe
 * the same thing the "Refresh now" button clears — derived from the store, so it
 * cannot drift from the code the way the previous hard-coded lists did.
 */
export function CachedApiTargetsList({ className }: CachedApiTargetsListProps) {
  const { t } = useI18nText();
  const [summaries, setSummaries] = useState<AppCachePrefixSummary[] | null>(null);

  // Poll once a second so the per-prefix countdowns tick alongside the header's.
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const next = await getAppCachePrefixSummaries();
      if (!cancelled) setSummaries(next);
    };
    void tick();
    const id = window.setInterval(() => void tick(), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  if (summaries === null) {
    return (
      <p className={cn('cache-targets__empty ggc-text-caption-s', className)}>
        {t('Checking...', '확인 중...')}
      </p>
    );
  }

  if (summaries.length === 0) {
    return (
      <p className={cn('cache-targets__empty ggc-text-caption-s', className)}>
        {t('Nothing cached right now.', '현재 캐시된 데이터가 없습니다.')}
      </p>
    );
  }

  return (
    <ul className={cn('cache-targets ggc-text-caption-s', className)}>
      {summaries.map((summary) => {
        const label = PREFIX_LABELS[summary.prefix];
        const expired = summary.minRemainingMs === null;
        return (
          <li key={summary.prefix} className="cache-targets__row">
            <span className="cache-targets__label">
              {label ? t(label[0], label[1]) : summary.prefix.replace(/::$/, '')}
              {summary.entries > 1 && (
                <span className="cache-targets__count"> ×{summary.entries}</span>
              )}
            </span>
            <code className="cache-targets__prefix">{summary.prefix.replace(/::$/, '')}</code>
            <span className={cn('cache-targets__ttl', expired && 'cache-targets__ttl--expired')}>
              {expired ? t('Expired', '만료됨') : formatCacheCountdownMs(summary.minRemainingMs ?? 0)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
